/**
 * `src/platform/` — runtime config (D-15′). **This file is the sole copy.**
 *
 * Replaces build-time `import.meta.env.VITE_API_URL` with a value read from the
 * nginx-emitted `window.__ZION_CP_API_URL__` global, so ONE mirrored image serves every
 * deployment/host. Default `""` = same-origin.
 *
 * S4: a runtime API base may only be same-origin OR in the injectable allowlist below (set
 * via `setApiOriginAllowlist`). Anything else is rejected (logged) and falls back to
 * same-origin — a mis-set or hostile `branding.json`/global can never point auth traffic
 * off-domain.
 *
 * ⛔ The allowlist is an INJECTABLE module `let` with an exported setter, NOT a compile-time
 * `readonly []`. That is an earned shape, not a style choice: with a hardcoded empty const the
 * `apiOriginAllowlist.includes(u.origin)` branch is DEAD — un-coverable in a package held at
 * 100% branches — and the S4 "an allowlisted origin passes" test becomes unimplementable. The
 * `let` + setter makes the branch reachable both at runtime (a deployment fronting a
 * cross-origin API base calls it once at boot) and in tests (push one origin, assert it
 * passes, reset).
 */
declare global {
  interface Window {
    __ZION_CP_API_URL__?: string;
  }
}

let apiOriginAllowlist: readonly string[] = [];

/** Set the cross-origin API-base allowlist. Empty by default — CP serves the
 *  SPA and API on the same origin behind Istio. A deployment that fronts a
 *  cross-origin API base calls this once at boot; the S4 test uses it to
 *  exercise the allowlisted-origin branch. */
export function setApiOriginAllowlist(origins: readonly string[]): void {
  apiOriginAllowlist = origins;
}

export function getApiBaseUrl(): string {
  const raw = (typeof window !== 'undefined' && window.__ZION_CP_API_URL__) || '';
  if (raw === '') return '';
  try {
    // Parse with NO base fallback: `apiUrl` must be an ABSOLUTE http(s) URL.
    // A scheme-less/relative value (e.g. `api.example.com`) would otherwise
    // resolve against the origin and be mistaken for same-origin, then returned
    // as a broken relative base — so require absolute and let anything else
    // throw → caught → '' (same-origin default).
    const u = new URL(raw);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return '';
    const sameOrigin = u.origin === window.location.origin;
    if (sameOrigin || apiOriginAllowlist.includes(u.origin)) {
      return raw.replace(/\/+$/, ''); // strip trailing slash(es) for clean joins
    }
    console.warn(`[runtime-config] rejected cross-origin API base: ${u.origin}`);
    return '';
  } catch {
    return '';
  }
}
